import {
  fetchUser,
  fetchUsers,
  getItemFromCollection,
  getItemsFromCollection,
} from "directus/src/operations";

import { ComboboxDemo } from "components/api-combobox";
import { Label } from "ui/components/label";
import { RenderProfile } from "components/render-profile";
import classNames from "classnames";
import client from "directus/src/client";
import { searchName } from "@/api/utils";
import { useFetch } from "@/hooks/useFetch";
import { useState } from "react";

// Predefined transformers for different collection types
const getTransformer = (collection) => {
  // Name-based collections (clients, companies, projects, jobs, billables, milestones)
  const nameBasedCollections = [
    "clients",
    "companies",
    "projects",
    "jobs",
    "billables",
    "milestones",
  ];

  if (nameBasedCollections.includes(collection)) {
    return (item) => ({
      label: item.name,
      value: item.id,
      select: item.name,
    });
  }

  // Special collections
  switch (collection) {
    case "users":
      return (item) => ({
        label: <RenderProfile user={item} />,
        value: item.id,
        select: <RenderProfile user={item} />,
      });

    default:
      return null; // Will use auto-detection
  }
};

// Helper function to create search filter based on field types
const createSearchFilter = (search, fields) => {
  if (!search || !fields.length) return {};

  const searchTerms = search
    .trim()
    .split(" ")
    .filter((term) => term);
  if (searchTerms.length === 0) return {};

  // Handle different field combinations
  if (fields.includes("first_name") && fields.includes("last_name")) {
    // User-like search: search across first_name and last_name

    return searchName(search);
  }

  if (fields.includes("name")) {
    // Simple name search
    return {
      name: { _icontains: search },
    };
  }

  if (fields.includes("title")) {
    // Title search
    return {
      title: { _icontains: search },
    };
  }

  if (fields.includes("label")) {
    // Label search
    return {
      label: { _icontains: search },
    };
  }

  // Fallback: search in the first available field
  const firstField = fields.find(
    (field) =>
      field !== "id" &&
      ["name", "title", "first_name", "last_name", "email", "label"].includes(
        field,
      ),
  );

  if (firstField) {
    return {
      [firstField]: { _icontains: search },
    };
  }

  return {};
};

// Factory function to create a Directus combobox for any collection
export function createDirectusCombobox(
  collection,
  defaultFields = ["id", "name"],
) {
  return function DirectusCombobox({
    fields = defaultFields,
    filters = {},
    value,
    onChange,
    placeholder = "Select",
    searchPlaceholder = "Search",
    label,
    required = false,
    className,
    transformer,
    addButton,
    dependenton = true,
    disabled = false,
  }) {
    const [search, setSearch] = useState("");

    // Determine the appropriate fetch functions based on collection
    const specialCollections = {
      users: {
        listFn: fetchUsers,
        itemFn: fetchUser,
      },
    };

    const isSpecialCollection = specialCollections[collection];
    const listFetchFn = isSpecialCollection?.listFn || getItemsFromCollection;
    const itemFetchFn = isSpecialCollection?.itemFn || getItemFromCollection;

    // Auto-detect search field based on available fields
    const getSearchField = () => {
      if (fields.includes("name")) return "name";
      if (fields.includes("title")) return "title";
      if (fields.includes("first_name")) return "first_name";
      if (fields.includes("last_name")) return "last_name";
      if (fields.includes("email")) return "email";
      if (fields.includes("label")) return "label";
      return "name"; // fallback
    };

    const searchField = getSearchField();

    const query = {
      fields,
      filter: {
        ...filters,
        ...createSearchFilter(search, fields),
      },
      sort: [searchField],
      limit: 10,
    };

    const {
      data: items,
      isLoading,
      isError,
    } = useFetch({
      queryKey: [collection, filters, search],
      queryFn: async () => {
        const response = isSpecialCollection
          ? await listFetchFn(query)
          : await listFetchFn(collection, query);
        return response || [];
      },
      enabled: !!dependenton,
    });

    // Get the appropriate transformer
    const defaultTransformer = getTransformer(collection);
    const finalTransformer = transformer || defaultTransformer;

    // Transform items for the combobox
    const transformedItems =
      items?.map((item) => {
        if (finalTransformer) {
          return finalTransformer(item);
        }

        // Auto-detect display field based on available fields
        let displayValue = "";

        // Try common name fields in order of preference
        if (item.name) {
          displayValue = item.name;
        } else if (item.title) {
          displayValue = item.title;
        } else if (item.first_name && item.last_name) {
          displayValue = `${item.first_name} ${item.last_name}`.trim();
        } else if (item.first_name) {
          displayValue = item.first_name;
        } else if (item.last_name) {
          displayValue = item.last_name;
        } else if (item.email) {
          displayValue = item.email;
        } else if (item.label) {
          displayValue = item.label;
        } else {
          // Fallback: use the first non-id field
          const nonIdFields = Object.keys(item).filter(
            (key) =>
              key !== "id" &&
              typeof item[key] === "string" &&
              item[key].trim() !== "",
          );
          displayValue =
            nonIdFields.length > 0 ? item[nonIdFields[0]] : `Item ${item.id}`;
        }

        return {
          label: displayValue,
          value: item.id,
          select: displayValue,
        };
      }) || [];

    // Add "Add New" button if provided
    const itemsWithAddButton = addButton
      ? [
          ...transformedItems,
          { label: addButton.label, value: "add-new", select: addButton.label },
        ]
      : transformedItems;

    // Create a generic fetch function for the collection
    const fetchItem = async (params) => {
      try {
        // Extract the ID from params (assuming it's the first key-value pair)
        const id = Object.values(params)[0];
        const response = isSpecialCollection
          ? await itemFetchFn(id, { fields })
          : await itemFetchFn(collection, id, { fields });

        return response || null;
      } catch (error) {
        console.error("Error fetching item:", error);
        return null;
      }
    };

    const handleChange = (selectedItem) => {
      if (selectedItem?.value === "add-new") {
        addButton?.onClick();
        return;
      }

      onChange(selectedItem?.value || "");
    };

    return (
      <div className={classNames("space-y-2", className)}>
        {label && (
          <Label
            className={classNames(
              "text-sm",
              required && "after:content-['*'] after:ml-0.5 after:text-red-500",
            )}
          >
            {label}
          </Label>
        )}

        <ComboboxDemo
          data={itemsWithAddButton}
          value={value}
          onChange={handleChange}
          search={search}
          onSearch={setSearch}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          isLoading={isLoading}
          disabled={disabled}
          transformer={finalTransformer}
          fetchFn={fetchItem}
          fetchKey="id"
          fetchParams={query}
        />
      </div>
    );
  };
}

// Pre-created comboboxes for common collections
export const CompanyCombobox = createDirectusCombobox("companies");
export const ClientCombobox = createDirectusCombobox("clients");
export const UserCombobox = createDirectusCombobox("users", [
  "id",
  "first_name",
  "last_name",
  "avatar",
]);
export const CountryCombobox = createDirectusCombobox("countries");
export const ProjectCombobox = createDirectusCombobox("projects");
export const JobCombobox = createDirectusCombobox("jobs");

// Generic DirectusCombobox for any collection
export function DirectusCombobox({ collection, ...props }) {
  const ComboboxComponent = createDirectusCombobox(collection);
  return <ComboboxComponent {...props} />;
}
